import { TabDock, useStore } from "@thekrystalship/krystal";
import { useAssistant } from "./AssistantDockContext.jsx";
import { can } from "../lib/persona.js";
import { mergedInbox } from "../lib/notifications.js";
import { PLACES, SECTIONS, placeHolds, placesOf } from "../lib/router.js";
import { PREF_KEYS, prefsStore } from "../lib/stores/prefs.js";

// The phone's way around: the design system's tab dock over the router's places. The bar is home in
// the middle and four free slots; the sheet holds every place the person's role holds, in the
// router's sections. A slot naming a place this person does not hold stays empty.

// The free slots when the person has chosen none, left to right: ids of the router's places.
const DEFAULT_SLOTS = ["servers", "alerts", "assistant", "profile"];

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// The kept choice as a list of four ids, or null for the defaults. The empty list is how the
// defaults are kept: the node refuses a null value, and anything but four ids reads as no choice.
function parseChoice(raw) {
  try {
    const slots = JSON.parse(raw);
    return Array.isArray(slots) && slots.length === DEFAULT_SLOTS.length && slots.every((id) => typeof id === "string") ? slots : null;
  } catch {
    return null;
  }
}

// What the dock reports, as what is kept: an empty slot takes a default no other slot holds, and a
// list equal to the defaults is the empty list.
function toChoice(ids) {
  const out = [...ids];
  for (let i = 0; i < out.length; i++) {
    if (out[i] && out.indexOf(out[i]) === i) continue;
    out[i] = DEFAULT_SLOTS.find((id) => !out.includes(id)) || DEFAULT_SLOTS[i];
  }
  return same(out, DEFAULT_SLOTS) ? [] : out;
}

function PhoneDock({ route, onNavigate, counts, online = true }) {
  const assistant = useAssistant();
  const { unread } = mergedInbox.useInbox();
  // A string, so the selector's answer is stable between renders; the store's local copy answers on
  // the first frame and a sync from another device lands here as a change.
  const kept = useStore(prefsStore, () => JSON.stringify(prefsStore.get(PREF_KEYS.TAB_BAR)));
  const slots = parseChoice(kept) || DEFAULT_SLOTS;

  const badges = { ...counts, notifications: { n: unread, tone: "info" } };
  const item = (place) => {
    const badge = badges[place.id];
    // A place only a node can draw is powered off while the entry node does not answer.
    const off = !online && !place.local;
    return {
      id: place.id,
      icon: place.icon,
      label: place.label,
      active: placeHolds(place, route) && !off,
      disabled: off,
      badge: badge ? badge.n : undefined,
      tone: badge ? badge.tone : undefined,
      onClick: () => onNavigate({ kind: place.kind }),
    };
  };
  const held = (place) => (!place.cap || can(place.cap)) && (place.id !== "assistant" || !!assistant);

  const sections = SECTIONS.map((section) => {
    const items = placesOf(section.id).filter(held).map(item);
    return { id: section.id, label: section.label, items };
  }).filter((section) => section.items.length);

  return (
    <TabDock
      home={item(PLACES.find((place) => place.id === "home"))}
      sections={sections}
      slots={slots}
      defaultSlots={DEFAULT_SLOTS}
      onSlotsChange={(ids) => prefsStore.set(PREF_KEYS.TAB_BAR, toChoice(ids))} />
  );
}

export { PhoneDock };
